import { Box, useTheme } from "@mui/material"
import { useEffect, useMemo, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { useAddInvoiceProductMutation } from "../../../newapi/financial/invoiceProductSlice";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import * as yup from "yup";
import ShValidatedInput from "../../theme/inputs/ShValidatedInput";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import { yupRequired } from "../../../utils/validation";
import EMGeneralExplainerBox from "../ModalsPopover/explainers/EMGeneralExplainerBox";
import { useGetAccountingSoftwareLedgersSelectQuery, useGetAccountingSoftwareTaxRatesSelectQuery } from "../../../newapi/financial/accountingSoftwareSlice";
import { useGetInvoiceProductCategorySelectQuery } from "../../../newapi/financial/invoiceProductCategorySlice";
import ShSwitch from "../../theme/inputs/ShSwitch";
import { getInvoiceProductTypeKey, getInvoiceProductTypeOptions } from "../../../utils/staticEnums";

const MAddInvoiceProduct = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [addInvoiceProduct,{isLoading}] = useAddInvoiceProductMutation();

    const [invoiceProductInfo, setInvoiceProductInfo] = useState({
        name: "",
        type: "",
        subType: "",
        invoiceline: "",
        invoicelineEN: "",
        ledgerId: 0,
        taxRateId: 0,
        invoiceProductCategoryId: null
    });

    const handleClose = () => {
        setInvoiceProductInfo({
            name: "",
            type: "",
            subType: "",
            invoiceline: "",
            invoicelineEN: "",
            ledgerId: 0,
            taxRateId: 0,
            invoiceProductCategoryId: null
        });
        props.handleClose();
    }

    // A caller that knows what the product is for hands the type pair in through `preset`,
    // so the dropdown opens on it; the person can still pick another.
    useEffect(() => {
        if (props.open && props.preset?.type) {
            setInvoiceProductInfo(prev => ({
                ...prev,
                type: props.preset.type,
                subType: props.preset.subType ?? ""
            }));
        }
    }, [props.open, props.preset?.type, props.preset?.subType]);

    const { data: taxRates, taxRatesIsLoading } = useGetAccountingSoftwareTaxRatesSelectQuery(0);
    const { data: ledgers, ledgersIsLoading } = useGetAccountingSoftwareLedgersSelectQuery(0);
    const { data: invoiceProductCategories, invoiceProductCategoriesIsLoading } = useGetInvoiceProductCategorySelectQuery();

    const [fieldErrors, setFieldErrors] = useState({});

    const rules = {
        name: yupRequired('name', t),
        type: yupRequired('type', t),
        subType: yupRequired('sub_type', t),
        invoiceline: yupRequired('invoiceline', t)
    };
    
    const schema = yup.object().shape(rules);
    
    const SaveInvoiceProductInfo = async (invoiceProductInfo, props) => {
        try {
            if (isLoading) return;

            setFieldErrors({});
            await schema.validate(invoiceProductInfo, { abortEarly: false });
            const data = await addInvoiceProduct(invoiceProductInfo).unwrap()
            showToastMessagePostRequest(t, data);

            handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                // Create an errors object where keys are field names and values are error messages
                const errors = err.inner.reduce((acc, currentError) => ({
                  ...acc,
                  [currentError.path]: currentError.message
                }), {});
          
                // Set the validation errors in the state
                setFieldErrors(errors);
              } else {
                // Handle other errors, such as network errors
                showToastMessageRequestError(t("create_failed"), t, err);
              }
        }
    };
    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleChange = (key, value) => {
        setInvoiceProductInfo(prev => ({
          ...prev,
          [key]: value
        }));

        setFieldErrors({
            ...fieldErrors,
            [key]: ''
          });
      };

    const typeOptions = useMemo(() => getInvoiceProductTypeOptions(t, invoiceProductInfo), [t, invoiceProductInfo.type, invoiceProductInfo.subType]);

    const handleTypeChange = (name, key) => {
        const option = typeOptions.find(item => item.key === key);

        setInvoiceProductInfo(prev => ({
            ...prev,
            type: option?.type ?? "",
            subType: option?.subType ?? ""
        }));

        setFieldErrors({
            ...fieldErrors,
            type: '',
            subType: ''
        });
    };

    return(
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box 
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}>
                <Subheading>{t("add_invoice_product")}</Subheading>
            </Box>

            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    flexDirection:"column",
                    gap:2
                }}
            >
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >
                    
                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                        }}
                    >
                        <Text>{t("name")}</Text>
                    </Box>
                    
                    
                    <ShValidatedInput
                            name="name"
                            value={invoiceProductInfo.name}
                            onChange={handleChange}
                            error={fieldErrors.name}
                        />
                            
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >
                    
                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        <Text>{t("invoiceline")}</Text>
                        <EMGeneralExplainerBox title={t('em_invoiceproductinvoiceline_title')} text={t('em_invoiceproductinvoiceline_text')}/>
                    </Box>
                    
                    
                    <ShValidatedInput
                            name="invoiceline"
                            value={invoiceProductInfo.invoiceline}
                            onChange={handleChange}
                            error={fieldErrors.invoiceline}
                        />
                            
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >
                    
                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        <Text>{t("invoiceline_en")}</Text>
                        <EMGeneralExplainerBox title={t('invoiceline_en')} text={t('em_invoiceline_en_text')}/>
                    </Box>
                    
                    
                    <ShValidatedInput
                            name="invoicelineEN"
                            value={invoiceProductInfo.invoicelineEN}
                            onChange={handleChange}
                            error={fieldErrors.invoicelineEN}
                        />
                            
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >
                    
                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                        }}
                    >
                        <Text>{t("type")}</Text>
                    </Box>
                    
                    
                    <ShValidatedInputDropdown
                        name="type"
                        displayName="displayName"
                        valueKey="key"
                        selected={getInvoiceProductTypeKey(invoiceProductInfo.type, invoiceProductInfo.subType)}
                        options={typeOptions}
                        noSelection={t("choose_option")} 
                        onChange={handleTypeChange}
                        error={fieldErrors.type ?? fieldErrors.subType}
                        closeOnSelection
                        disallowDeselection
                        searchOptions={{
                            enabled: true,
                            placeholder: t("search")
                        }}
                    />
                            
                </Box>
                <Box
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >

                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                        }}
                    >
                        <Text>{t("product_category")}</Text>
                    </Box>

                    <ShValidatedInputDropdown
                        name="invoiceProductCategoryId"
                        displayName="name"
                        changeField="id"
                        selected={invoiceProductCategories ? invoiceProductCategories.findIndex(item => item.id === invoiceProductInfo?.invoiceProductCategoryId) ?? -1 : -1}
                        options={invoiceProductCategories ?? []}
                        noSelection={t("choose_option")}
                        onChange={handleChange}
                        error={fieldErrors.invoiceProductCategoryId}
                    />
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >
                    
                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                        }}
                    >
                        <Text>{t("ledger")}</Text>
                    </Box>
                    
                    <ShValidatedInputDropdown
                        name="ledgerId"
                        displayName="description"
                        changeField="id"
                        selected={ledgers ? ledgers.findIndex(item => item.id === invoiceProductInfo?.ledgerId) ?? -1 : -1}
                        options={ledgers ?? []}
                        noSelection={t("choose_option")} 
                        onChange={handleChange}
                        error={fieldErrors.type}
                    />   
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >
                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                        }}
                    >
                        <Text>{t("tax_rate")}</Text>
                    </Box>
                    <ShValidatedInputDropdown
                        name="taxRateId"
                        displayName="description"
                        changeField="id"
                        selected={taxRates ? taxRates.findIndex(item => item.id === invoiceProductInfo?.taxRateId) ?? -1 : -1}
                        options={taxRates ?? []}
                        noSelection={t("choose_option")} 
                        onChange={handleChange}
                        error={fieldErrors.taxRateId}
                    />     
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >
                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                            display: "flex", 
                            alignItems: "center"
                        }}
                    >
                        <Text>{t("include_fuel_surcharges")}</Text>
                        <EMGeneralExplainerBox title={t('include_fuel_surcharges')} text={t('em_include_fuel_surcharges_text')}/>
                    </Box>
                    <ShSwitch
                        checked={invoiceProductInfo?.includeFuelSurcharges ?? false}
                        onChange={(e) => handleChange('includeFuelSurcharges', e.target.checked)}
                    />
                </Box>
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7,
                        alignItems:"flex-start"
                    }}
                >
                    <Box
                        sx={{
                            minWidth:210,
                            pt:1,
                            display: "flex", 
                            alignItems: "center"
                        }}
                    >
                        <Text>{t("merge_lines")}</Text>
                        <EMGeneralExplainerBox title={t('merge_lines')} text={t('em_merge_lines_text')}/>
                    </Box>
                    <ShSwitch
                        checked={invoiceProductInfo?.mergeLines ?? false}
                        onChange={(e) => handleChange('mergeLines', e.target.checked)}
                    />
                </Box>
            </Box>
            <Box
                component="footer"
                sx={{
                    
                    display:"block",
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton isLoading={isLoading} className={"group-btn"} color={"green"}
                    variant="contained" 
                    onClick={() => SaveInvoiceProductInfo(invoiceProductInfo, props)}
                    >{t("add")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MAddInvoiceProduct;
